import axios from "axios";
import { logger } from "../utils/logger";
// eslint-disable-next-line no-undef

const getBaseUrl = () => {
	switch (process.env.REACT_APP_ENVIRONMENT) {
		case "development":
	    return process.env.REACT_APP_API_BASE_URL
		case "staging":
			return "http://nginx/api"
		case "testing":
			return "https://test.plasticise.com.au/api"
		case "production":
			return "https://app.plasticise.com.au/api"
		default:
			throw new Error("REACT_APP_ENVIRONMENT not set correctly")
	}
}

const api = axios.create({
  baseURL: getBaseUrl(),
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue = [];

const redirectToLogin = () => {
  // Only redirect if not already at login path
  if (window.location.pathname !== "/") {
    localStorage.clear(); // Clear tokens before redirect
    window.location.href = "/";
  }
};

const processQueue = (error, token = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use(
  (config) => {
    const correlationId = logger.getCorrelationId();
    config.headers["X-Correlation-ID"] = correlationId;

    const isAuthEndpoint =
      config.url?.includes("/token/") ||
      config.url?.includes("/login") ||
      config.url === "/signup/" ||
      config.url === "/signup/start-trial/" ||
      config.url?.includes("/reset-password/") ||
      config.url?.includes("/reset-password-validate/") ||
      config.url?.includes("/reset-password-confirm/");

    // Skip token checks for auth endpoints
    if (isAuthEndpoint) {
      return config;
    }

    // Only check refresh token for non-auth endpoints
    const refreshToken = localStorage.getItem("refresh_token");
    if (!refreshToken) {
      redirectToLogin();
      return Promise.reject(new Error("No refresh token available"));
    }

    const accessToken = localStorage.getItem("access_token");
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
      // eslint-disable-next-line no-undef
      if (process.env.REACT_APP_NGROK === "true") {
        config.headers["ngrok-skip-browser-warning"] = "true";
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    // Extract correlation ID from response if present
    const correlationId = response.headers["x-correlation-id"];
    if (correlationId) {
      logger.setCorrelationId(correlationId);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const refreshToken = localStorage.getItem("refresh_token");
    if (!error.response) {
      logger.error("Network Error", {
        url: error.config?.url,
        method: error.config?.method,
        timestamp: new Date().toISOString(),
        message: error.message,
        stack: error.stack,
      });
      return Promise.reject(new Error("Network connectivity issue"));
    }
    if (error.response !== 401) {
      logger.error(`API Error ${error.response.status}`, {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
        correlationId: error.config?.headers["X-Correlation-ID"],
      });
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/token/refresh/")
    ) {
      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      if (!isRefreshing) {
        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const response = await api.post(
            "/token/refresh/",
            { refresh: refreshToken },
            {
              headers: { "Content-Type": "application/json" },
              withCredentials: true,
            }
          );

          if (response.status === 200) {
            localStorage.setItem("access_token", response.data.access);
            axios.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${response.data.access}`;
            processQueue(null, response.data.access);
            isRefreshing = false;
            originalRequest.headers[
              "Authorization"
            ] = `Bearer ${response.data.access}`;
            return axios(originalRequest);
          }
        } catch (err) {
          logger.error("Error refreshing token", {
            error: err,
            url: originalRequest.url,
          });
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axios(originalRequest);
        })
        .catch((err) => {
          logger.error("Error running intercepted request", {
            error: err,
            url: originalRequest.url,
          });
          return Promise.reject(err);
        });
    }

    return Promise.reject(error);
  }
);

export default api;
